import {Injectable} from '@angular/core';
import {ListView,TypeComparaison} from "@domain/common/list-view";
import {FraisReferentielItemComponent} from "@components/admin/frais/referentiel/frais-referentiel-item/frais-referentiel-item.component";
import {Referentiel} from "@domain/admin/referentiels/referentiel";
import {TranslateService} from "@ngx-translate/core";
import {TypeReferentiel} from "@domain/admin/referentiels/type-referentiel";
import {FraisReferentielComponent} from "@components/admin/frais/referentiel/frais-referentiel.component";
import {ReferentielMcc} from "@domain/admin/referentiels/referentiel-mcc";
import {Sorting} from '@domain/common/list-view/sorting';

/**
 * Service de gestion du frais référentiel
 *
 * @author Lionel Gimenez
 * @date 13/05/2025
 */
@Injectable()
export class FraisReferentielService {

    /** Liste des listView pour la mise en cache */
    referentielListViewListe: Map<TypeReferentiel,ListView<Referentiel,FraisReferentielItemComponent>> = new Map();

    /** Liste des listView MCC pour la mise en cache */
    referentielMccListViewListe: Map<TypeReferentiel,ListView<ReferentielMcc,FraisReferentielItemComponent>> = new Map();

    /**
     * Constructeur
     */
    constructor(
        private translateService: TranslateService
    ) {
    }

    /**
     * Chargement d'une listView en cache si existante
     */
    loadListView(typeReferentiel: TypeReferentiel): ListView<any,FraisReferentielItemComponent> {
        //Si c'est un code MCC, on utilise la méthode spécifique
        if (typeReferentiel === TypeReferentiel.CODES_MCC) {
            return this.loadListViewMcc(typeReferentiel);
        }

        //Si une ListView existe déjà en cache pour ce type de référentiel
        if (this.referentielListViewListe.has(typeReferentiel)) {
            //On la retourne directement
            return this.referentielListViewListe.get(typeReferentiel)
        } else {
            //Sinon, on initialise une nouvelle ListView et on la retourne
            return this.initListView(typeReferentiel);
        }
    }

    /**
     * Chargement d'une listView MCC en cache si existante
     */
    loadListViewMcc(typeReferentiel: TypeReferentiel): ListView<ReferentielMcc,FraisReferentielItemComponent> {
        //Si une ListView existe déjà en cache pour ce type de référentiel
        if (this.referentielMccListViewListe.has(typeReferentiel)) {
            //On la retourne directement
            return this.referentielMccListViewListe.get(typeReferentiel)
        } else {
            //Sinon, on initialise une nouvelle ListView et on la retourne
            return this.initListViewMcc(typeReferentiel);
        }
    }

    /**
     * Chargement d'une listView
     */
    initListView(typeReferentiel: TypeReferentiel): ListView<Referentiel,FraisReferentielItemComponent> {
        const listView = new ListView<Referentiel,FraisReferentielItemComponent>({
            uri: '/controller/Referentiel/loadFraisReferentiel',
            component: FraisReferentielItemComponent,
            defaultOrder: "ordre,libelle",
            title: this.translateService.instant(FraisReferentielComponent.getTitleByType(typeReferentiel)),
            isFilter: true,
            extraOptions: {
                listeTabItems: FraisReferentielComponent.listeOnglets
            },
            listeFilters: [
                {
                    clef: 'libelle',
                    title: this.translateService.instant('admin.entreprise.referentiels.referentielFiltres.libelle'),
                    isDefault: true,
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE]
                },{
                    clef: 'code',
                    title: this.translateService.instant('admin.entreprise.referentiels.referentielFiltres.code'),
                    isDefault: true,
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE]
                },{
                    clef: 'ordre',
                    title: this.translateService.instant('admin.entreprise.referentiels.referentielFiltres.ordre'),
                    isDefault: true,
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE]
                },
            ]
        });

        //Définition des colonnes de tri
        listView.columns = [
            {key: 'ordre',title: 'admin.entreprise.referentiels.referentielFiltres.ordre'},
            {key: 'code',title: 'admin.entreprise.referentiels.referentielFiltres.code'},
            {key: 'libelle',title: 'admin.entreprise.referentiels.referentielFiltres.libelle'},
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        listView.sorting = new Sorting(listView.columns,"ordre,libelle");

        //On force un filtre pour le type de référentiel même si l'utilisateur le change
        listView.listeStaticFilters.push({
            clef: 'type',
            listeObjects: [typeReferentiel],
            typeComparaison: TypeComparaison[TypeComparaison.IN],
            hiddenChip: true
        });

        //Persistence des filtres
        listView.isPersistFilters = true;

        //Nom de la classe
        listView.className = 'FraisReferentiel' + typeReferentiel;

        //On enregistre la liste en cache
        this.referentielListViewListe.set(typeReferentiel,listView);

        //On retourne la list initialisée
        return listView;
    }

    /**
     * Initialisation d'une listView spécifique pour les codes MCC
     */
    initListViewMcc(typeReferentiel: TypeReferentiel): ListView<ReferentielMcc,FraisReferentielItemComponent> {
        const listView = new ListView<ReferentielMcc,FraisReferentielItemComponent>({
            uri: '/controller/Referentiel/loadFraisReferentielMcc',
            component: FraisReferentielItemComponent,
            title: this.translateService.instant(FraisReferentielComponent.getTitleByType(typeReferentiel)),
            isFilter: true,
            defaultOrder: 'libelle',
            mapResult: (result: any) => {
                if (result && result.listeResultats) {
                    result.listeResultats.forEach(item => {
                        item.type = 'C'; //C pour MCC, comme défini dans le composant frais-referentiel-item
                    });
                }
                return result;
            },
            extraOptions: {
                listeTabItems: FraisReferentielComponent.listeOnglets
            },
            listeFilters: [
                {
                    clef: 'libelle',
                    title: this.translateService.instant('admin.frais.referentiels.mcc.libelle'),
                    isDefault: true,
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE]
                },{
                    clef: 'code',
                    title: this.translateService.instant('admin.frais.referentiels.mcc.code'),
                    isDefault: true,
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE]
                },{
                    clef: 'libelleGroupe',
                    title: this.translateService.instant('admin.frais.referentiels.mcc.rattachement'),
                    isDefault: true,
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE]
                }
            ]
        });

        //Définition des colonnes de tri
        listView.columns = [
            {key: 'libelle',title: 'admin.frais.referentiels.mcc.libelle'},
            {key: 'code',title: 'admin.frais.referentiels.mcc.code'},
            {key: 'libelleGroupe',title: 'admin.frais.referentiels.mcc.rattachement'}
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        listView.sorting = new Sorting(listView.columns,"libelle");

        //Persistence des filtres
        listView.isPersistFilters = true;

        //Nom de la classe
        listView.className = 'FraisReferentielMcc';

        //On enregistre la liste en cache
        this.referentielMccListViewListe.set(typeReferentiel,listView);

        //On retourne la list initialisée
        return listView;
    }
}